<!--@format-->
<template>
    <div class="profile-contact-settings-widget">
        <div class="widget-header">
            <h3>Edit Contact Information</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isLoading" class="loading-card">
            <loading-svg />
        </div>
        <div v-else class="content-wrapper">
            <div v-if="user" class="contact-information">
                <div class="name-container">
                    <div class="Name info-container">
                        <app-input v-model="user.firstName" :label="'First Name'" />
                    </div>
                    <div class="Name info-container">
                        <app-input v-model="user.lastName" :label="'Last Name'" />
                    </div>
                </div>
                <div class="title info-container">
                    <app-input v-model="user.title" :label="'Job Title'" />
                </div>
                <div class="email info-container">
                    <app-input v-model="user.email" :label="'Email'" />
                </div>
                <div class="phone info-container">
                    <app-input v-model="user.phone" :label="'Phone'" />
                </div>
                <div class="birthday info-container">
                    <label>Birthday</label>
                    <date-picker v-model="user.birthday" />
                </div>
                <div class="linkedin info-container">
                    <app-input v-model="user.linkedinUrl" :label="'LinkedIn'" />
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button @click="save()"> Save </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input');
const DatePicker = () => import('@/components/common/date-picker');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'profile-contact-settings-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'date-picker': DatePicker,
        'loading-svg': LoadingSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            user: {
                firstName: '',
                lastName: '',
                title: '',
                email: '',
                phone: '',
                birthday: '',
                linkedinUrl: '',
            },
            isLoading: false,
        };
    },

    created() {
        this.getUser();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getUser() {
            this.isLoading = true;
            this.$api
                .get(`/api/Users/${this.widget.userId}/profile`)
                .then((response) => {
                    var user = response.data;
                    this.user = {
                        firstName: user.FirstName,
                        lastName: user.LastName,
                        title: user.Title,
                        email: user.Email,
                        phone: user.PhoneNumber,
                        birthday: user.Birthdate ? this.$moment(user.Birthdate).toDate() : null,
                        linkedinUrl: user.LinkedinUrl,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Failed to get user information.`, { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        save() {
            this.isLoading = true;
            var changes = {
                FirstName: this.user.firstName,
                LastName: this.user.lastName,
                Title: this.user.title,
                Email: this.user.email,
                PhoneNumber: this.user.phone,
                Birthdate: this.user.birthday
                    ? this.$moment(this.user.birthday).utc().format('YYYY-MM-DDTHH:mm') + 'z'
                    : null,
                LinkedinUrl: this.user.linkedinUrl,
            };

            this.$api
                .put(`/api/Users/${this.widget.userId}/profile`, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification('User successfully updated.');
                    EventBus.$emit('profile:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('User failed to update.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.profile-contact-settings-widget .date-picker .vdp-datepicker input {
    background: var(--color-fill-field-active);
    font: normal 16px/1em 'Roboto';
    width: 100%;
    border: 2px solid var(--color-border-field-active);
    padding: 7px 15px;
    border-radius: 20px;
    color: var(--color-text-default);
    outline: none;
    cursor: pointer;
}

.profile-contact-settings-widget .date-picker .vdp-datepicker input:focus-within {
    background: var(--color-fill-field-input);
    border: 2px solid var(--color-border-field-input);
}

.profile-contact-settings-widget .date-picker .vdp-datepicker:hover input {
    background: var(--color-fill-field-hover);
    border: 2px solid var(--color-border-field-hover);
}

.profile-contact-settings-widget .date-picker .vdp-datepicker.form-control.minimal-text-field,
.profile-contact-settings-widget
    .date-picker
    .vdp-datepicker.form-control.minimal-text-field:hover {
    border: 0;
}

.profile-contact-settings-widget .date-picker .calendar-svg {
    margin-bottom: 15px;
    cursor: pointer;
    fill: var(--color-fill-accent-vividraspberry);
    position: absolute;
    right: 10px;
    top: 6px;
    max-width: 20px;
    width: 100%;
}

.profile-contact-settings-widget .info-container {
    margin-bottom: 20px;
}

.profile-contact-settings-widget
    .date-picker
    .vdp-datepicker.form-control.minimal-text-field:focus-within {
    border-bottom: none;
}

.profile-contact-settings-widget .cancel-btn {
    background: transparent;
    color: var(--color-border-button-cancel);
    border: none;
}

.profile-contact-settings-widget .cancel-btn:hover {
    background: var(--color-border-button-cancel-hover);
}

.profile-contact-settings-widget .name-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.profile-contact-settings-widget .name-container .info-container {
    max-width: 49%;
    width: 100%;
}
</style>
